import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import * as S from './styles'
import Image from 'next/image'
import { ENDPOINTS, sendPostRequest } from '@/services/api'
import { useModal } from '@/contexts/ModalContext'
import { flattenFolders, getEncodedImage } from './utils'
import Swal from 'sweetalert2'
import SmallLoading from '@/components/SmallLoading'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/translate/i18n'
import { listFoldersAvailableToAdd } from '@/services/api/collections'

interface Folder {
  id: string
  path: string[]
}

interface FoldersListProps {
  folders: Folder[]
  selectedOption: Folder | null
  changeSelectedOption: (folder: Folder) => void
  isSaving: boolean
  labels: Record<string, string>
}

function FoldersList({
  folders,
  selectedOption,
  changeSelectedOption,
  isSaving,
  labels
}: FoldersListProps) {
  return (
    <S.OptionsContainer>
      <S.OptionsList id="add-to-collection-folders-list">
        {folders.map(folder => (
          <li key={folder.id}>
            <S.Option
              id={`add-to-collection-folder-option-${folder.id}`}
              title={folder.path.join(' > ')}
              onClick={() => changeSelectedOption(folder)}
              className={selectedOption?.id === folder.id ? 'active' : ''}
              disabled={isSaving}
            >
              <Image
                src={
                  folder.id
                    ? '/assets/images/collection-folder.svg'
                    : '/assets/images/my-collection.svg'
                }
                width={18}
                height={18}
                alt={folder.id ? labels.folderAlt : labels.homeAlt}
              />
              <span>{folder.path.at(-1)}</span>
            </S.Option>
          </li>
        ))}
      </S.OptionsList>

      {!!selectedOption && (
        <S.OptionDescription>
          {selectedOption.path.map((folderName, index) => (
            <Fragment key={`slice-${selectedOption.id}-${index}`}>
              {!!index && <span className="material-icons">chevron_right</span>}
              <span>{folderName}</span>
            </Fragment>
          ))}
        </S.OptionDescription>
      )}
    </S.OptionsContainer>
  )
}

export default function AddToCollection({ config }) {
  const [foldersList, setFoldersList] = useState([])
  const [searchText, setSearchText] = useState('')
  const [selectedOption, setSelectedOption] = useState<Folder>()
  const [loadingState, setLoadingState] = useState({
    folders: true,
    save: false
  })
  const [labels, setLabels] = useState<Record<string, string>>({})

  const inputRef = useRef(null)

  const { closeModal } = useModal()

  const { t } = useTranslation()

  const getFoldersList = useCallback(async () => {
    setLoadingState(state => ({ ...state, folders: true }))

    try {
      const items = await listFoldersAvailableToAdd()

      const flattenedFolders = flattenFolders(items)

      setFoldersList([{ id: '', path: ['Minha coleção'] }, ...flattenedFolders])
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao obter as pastas',
        text: 'Não foi possível obter as pastas disponíveis. Tente novamente mais tarde',
        didClose() {
          closeModal()
        }
      })
    }

    setLoadingState(state => ({ ...state, folders: false }))
  }, [])

  const { contentId, contentName, contentImageUrl } = config

  const saveCollection = useCallback(async () => {
    setLoadingState(state => ({ ...state, save: true }))

    try {
      const encodedImage = await getEncodedImage(contentImageUrl)

      const body = {
        folder_id: selectedOption.id,
        content_id: contentId,
        name: contentName,
        properties: {
          convertedImage: encodedImage,
          isCreated: false
        }
      }

      const response = await sendPostRequest(ENDPOINTS.save_collection, body)

      if (response.status !== 200) {
        throw response
      }

      closeModal()

      Swal.fire({
        icon: 'success',
        title: i18n.t('messages.saveCollectionSuccessTitle'),
        text: i18n.t('messages.saveCollectionSuccessText')
      })
    } catch {
      Swal.fire({
        icon: 'error',
        title: i18n.t('messages.saveCollectionErrorTitle'),
        text: i18n.t('messages.saveCollectionErrorText')
      })
    }

    setLoadingState(state => ({ ...state, save: false }))
  }, [selectedOption, contentId, contentName])

  const filteredFoldersList = useMemo(() => {
    if (!foldersList.length) {
      return []
    }

    if (!searchText) {
      return foldersList
    }

    return foldersList.filter(folder => {
      const assembledPath = folder.path.join(',')

      return assembledPath.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [foldersList, searchText])

  useEffect(() => {
    getFoldersList()
  }, [getFoldersList])

  useEffect(() => {
    setLabels({
      addToMyCollectionTitle: t('titles.addToMyCollection'),
      selectFolderLabel: t('labels.selectFolder'),
      folderAlt: t('alts.folder'),
      homeAlt: t('alts.home'),
      cancelButton: t('buttons.cancel'),
      addButton: t('buttons.add'),
      folderNotFound: t('messages.folderNotFound')
    })
  }, [t])

  const isAddBtnDisabled =
    !selectedOption || loadingState.save || !filteredFoldersList.length

  return (
    <S.Container>
      <S.Header>
        <S.Title>{labels.addToMyCollectionTitle}</S.Title>
        <S.CloseButton
          id="add-to-collection-close-btn"
          onClick={closeModal}
          disabled={loadingState.save}
        >
          <span className="material-icons">close</span>
        </S.CloseButton>
      </S.Header>
      {loadingState.folders && <SmallLoading />}
      {!loadingState.folders && (
        <>
          <S.Content>
            <S.Field onClick={() => inputRef.current?.focus()}>
              <label htmlFor="add-to-collection-search-field">
                {labels.selectFolderLabel}
              </label>

              <div>
                <input
                  id="add-to-collection-search-field"
                  ref={inputRef}
                  onChange={event => setSearchText(event.target.value)}
                  value={searchText}
                  placeholder={t('labels.shearchFolder')}
                />

                <span className="material-icons">search</span>
              </div>
            </S.Field>

            {filteredFoldersList.length ? (
              <FoldersList
                folders={filteredFoldersList}
                selectedOption={selectedOption}
                changeSelectedOption={folder => {
                  setSelectedOption(folder)
                }}
                isSaving={loadingState.save}
                labels={labels}
              />
            ) : (
              <S.Field>
                <label>{labels.folderNotFound}</label>
              </S.Field>
            )}
          </S.Content>

          <S.Footer>
            <button
              id="add-to-collection-cancel-btn"
              onClick={closeModal}
              disabled={loadingState.save}
            >
              {labels.cancelButton}
            </button>
            <button
              id="add-to-collection-add-btn"
              disabled={isAddBtnDisabled}
              onClick={saveCollection}
            >
              <span>{labels.addButton}</span>
              {loadingState.save && <SmallLoading />}
            </button>
          </S.Footer>
        </>
      )}
    </S.Container>
  )
}
